import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AppstoreAddOutlined, BarChartOutlined, BorderOutlined, BuildOutlined, BulbOutlined, CloseCircleOutlined, CloudUploadOutlined, ClusterOutlined, CopyOutlined, FolderOpenOutlined, InboxOutlined, PictureOutlined, SaveOutlined, ScanOutlined, ShopOutlined, ShoppingCartOutlined, SnippetsOutlined, TableOutlined, ToolOutlined, UserOutlined } from '@ant-design/icons';
import { Button, ConfigProvider, Dropdown, Layout, Menu, Select, Space, theme } from 'antd';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { AuthContext } from './contexts/AuthContext';
import './AdminApp.css';
import { saveAs } from 'file-saver';
import { BASE_PATH } from '.';
//import ImportKingfisherAssets from './components/admin/tools/ImportKingfisherMetadata';
import ExportKingfisherProjects from './components/admin/tools/ExportKingfisherProjects';
import { ConfigContext } from './contexts/ConfigContext';
import { ClientDTO } from './openapi/requests';
import ImportClientAssets from './components/admin/tools/ImportClientAssets';
const { Content, Sider } = Layout;

type MenuItem = {
    url: string;
    icon: JSX.Element;
    label: string;
}

const AdminApp: React.FC = () => {
    const { hasRole, hasPermission, logOut, username } = useContext(AuthContext);
    const { availableClients, currentClient } = useContext(ConfigContext);
    const [importOpen, setImportOpen] = useState(false);
    const [exportProjectsOpen, setExportProjectsOpen] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState((window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches));
    const [selectedExportClient, setSelectedExportClient] = useState<ClientDTO>()

    const items: MenuItem[] = useMemo(() => {
        const menuItems: MenuItem[] = [];

        if (hasPermission('ViewOrders')) {
            menuItems.push({
                icon: <InboxOutlined />,
                url: '/admin/orders',
                label: 'Orders'
            });
            menuItems.push({
                icon: <ShoppingCartOutlined />,
                url: '/admin/projects',
                label: 'Projects'
            });
        }

        if (hasRole('Cadesign')) {
            menuItems.push({
                icon: <ShopOutlined />,
                url: '/admin/clients',
                label: 'Clients'
            });
            menuItems.push({
                icon: <CloudUploadOutlined />,
                url: '/admin/deliveryConfigurations',
                label: 'Delivery configurations'
            });
            menuItems.push({
                icon: <SaveOutlined />,
                url: '/admin/outputformats',
                label: 'Output Formats'
            });
            menuItems.push({
                icon: <BorderOutlined />,
                url: '/admin/materials',
                label: 'Materials'
            });
            menuItems.push({
                icon: <BuildOutlined />,
                url: '/admin/models',
                label: 'Models'
            });
            menuItems.push({
                icon: <FolderOpenOutlined />,
                url: '/admin/modelpacks',
                label: 'Modelpacks'
            });
            menuItems.push({
                icon: <ClusterOutlined />,
                url: '/admin/bommodels',
                label: 'BOMs'
            });
            menuItems.push({
                icon: <ScanOutlined />,
                url: '/admin/modifiers',
                label: 'Modifiers'
            });
            menuItems.push({
                icon: <PictureOutlined />,
                url: '/admin/scenes',
                label: 'Scenes'
            });
            menuItems.push({
                icon: <SnippetsOutlined />,
                url: '/admin/templates',
                label: 'Templates'
            });
            menuItems.push({
                icon: <AppstoreAddOutlined />,
                url: '/admin/assetprojects',
                label: 'Asset projects'
            });
            menuItems.push({
                icon: <CopyOutlined />,
                url: '/admin/pages',
                label: 'Pages'
            });
        }

        if (hasPermission('UserAdmin')) {
            menuItems.push({
                icon: <UserOutlined />,
                url: '/admin/users',
                label: 'Users'
            });
        }

        if (hasPermission('ViewStatistics')) {
            menuItems.push({
                icon: <BarChartOutlined />,
                url: '/admin/statistics',
                label: 'Statistics'
            });
        }

        return menuItems;
    }, [hasPermission, hasRole]);

    const location = useLocation();
    const [selectedKey, setSelectedKey] = useState(items.find(_item => location.pathname.startsWith(_item.url))?.url ?? "");

    useEffect(() => {
        if (currentClient.id > 0)
        setSelectedExportClient(currentClient)
    },[currentClient])
  
    useEffect(() => {
        setSelectedKey(items.find(_item => location.pathname.startsWith(_item.url))?.url ?? "")
    }, [items, location])

    const onExportClientChange = (value: any, option: any) => {
        setSelectedExportClient(availableClients.find(x => x.id === value))
    }

    const exportClientAssets = useCallback(() => {
        const token = localStorage.getItem('accessToken');

        fetch(`${BASE_PATH}/Export/client/assets/${selectedExportClient?.id}`, {
            method: 'get',
            headers: {
                "Authorization": 'Bearer ' + token
            }
        }).then(response => {
            return response.blob();
        }).then(blob => {
            saveAs(blob, "export.xlsx");
        });
    }, [selectedExportClient?.id])

   /* const exportAssets = useCallback(() => {
        const token = localStorage.getItem('accessToken');

        fetch(`${BASE_PATH}/Export/kingfisher/assets`, {
            method: 'get',
            headers: {
                "Authorization": 'Bearer ' + token
            }
        }).then(response => {
            return response.blob();
        }).then(blob => {
            saveAs(blob, "export.xlsx");
        });
    }, []);*/

    return (
        <ConfigProvider
            theme={{
                algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
            }}
        >
            <Layout style={{ minHeight: '100vh' }} hasSider id='adminapp'>
                <Sider width={200}>
                    <Menu
                        theme='dark'
                        mode="inline"
                        items={items.map(e => ({
                            key: e.url,
                            icon: e.icon,
                            label: <Link to={e.url}>{e.label}</Link>
                        }))}
                        selectedKeys={[selectedKey]}
                        style={{ height: '100%', borderRight: 0 }} />
                </Sider>
                <Layout style={{ alignSelf: 'flex-start' }}>
                    <header>
                        <ImportClientAssets clientId={selectedExportClient?.id ?? 0} isOpen={importOpen} onClose={() => setImportOpen(false)} />
                        <ExportKingfisherProjects isOpen={exportProjectsOpen} onClose={() => setExportProjectsOpen(false)} />
                        <Space>
                            <Select style={{ width: '200px'}} options={availableClients.map(x =>{ return { label: x.name, value: x.id }})} value={selectedExportClient?.id} onChange={onExportClientChange}></Select>
                            <Dropdown menu={{
                                items: [{
                                    label: 'Export client assets as excel',
                                    key: '1',
                                    icon: <TableOutlined />,
                                    onClick: exportClientAssets
                                }, {
                                    label: 'Import client assets from excel',
                                    key: '2',
                                    icon: <TableOutlined />,
                                    onClick: () => setImportOpen(true)
                                }, {
                                    label: 'Kingfisher project overview',
                                    key: '3',
                                    icon: <TableOutlined />,
                                    onClick: () => setExportProjectsOpen(true)
                                }]
                            }} placement="bottomRight" arrow={{ pointAtCenter: false }}>
                                <Button><ToolOutlined /> Tools</Button>
                            </Dropdown>
                            <Dropdown menu={{
                                items: [{
                                    label: 'Toggle dark mode',
                                    key: '1',
                                    icon: <BulbOutlined />,
                                    onClick: () => setIsDarkMode(e => !e)
                                },{
                                    label: 'Logout',
                                    key: '2',
                                    icon: <CloseCircleOutlined />,
                                    onClick: logOut
                                },]
                            }} placement="bottomRight" arrow={{ pointAtCenter: false }}>
                                <Button><UserOutlined /> {username}</Button>
                            </Dropdown>
                        </Space>
                    </header>
                    <Layout style={{ padding: 24 }}>
                        <Content
                            style={{
                                padding: 24,
                                margin: 0,
                                minHeight: 280,
                            }}
                        >
                            <Outlet />
                        </Content>
                    </Layout>
                </Layout>
            </Layout>
        </ConfigProvider>
    );
};

export default AdminApp;